import { useState } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, EstadoVacio, Seccion } from '@/components/Card';
import {
  verListaDeCompras,
  marcarAtendido,
  limpiarMarcas,
  type RenglonCompra,
} from '@/db/repos/shopping';
import { ETIQUETA_PERIODO, type PeriodoLista } from '@/domain/planning';
import { hoy, formatearMedio } from '@/domain/dates';
import { formatearCentavos } from '@/domain/money';

const PERIODOS: PeriodoLista[] = ['manana', 'proximos', 'semana'];

/**
 * Lista de compras: lo que falta para cocinar lo que esta en el menu.
 *
 * Un renglon por insumo, aunque lo pidan tres platillos. La cantidad ya viene
 * descontando lo que hay en inventario, que es la diferencia entre una lista
 * util y una lista de recetas.
 */
export function ShoppingListPage() {
  const [periodo, setPeriodo] = useState<PeriodoLista>('proximos');
  const fecha = hoy();

  const lista = useLiveQuery(() => verListaDeCompras(periodo, fecha), [periodo, fecha]);

  return (
    <div className="space-y-5">
      <PageHeader titulo="Lista de compras" descripcion="Lo que falta para cocinar tu menú" />

      <div className="flex gap-2">
        {PERIODOS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriodo(p)}
            aria-pressed={periodo === p}
            className={`min-h-11 flex-1 rounded-xl border px-2 text-sm font-medium ${
              periodo === p
                ? 'border-marca bg-marca text-white'
                : 'border-borde-fuerte bg-superficie text-texto active:bg-superficie-suave'
            }`}
          >
            {ETIQUETA_PERIODO[p]}
          </button>
        ))}
      </div>

      {lista === undefined && <p className="text-sm text-texto-suave">Cargando…</p>}

      {lista !== undefined && (
        <>
          <p className="text-xs text-texto-tenue">
            Del {formatearMedio(lista.desde)} al {formatearMedio(lista.hasta)} ·{' '}
            {lista.platillosConsiderados}{' '}
            {lista.platillosConsiderados === 1 ? 'platillo' : 'platillos'} por cocinar
          </p>

          {lista.platillosConsiderados === 0 && (
            <EstadoVacio
              titulo="No hay nada por cocinar en este periodo"
              descripcion="Pon platillos en el menú y aquí aparece lo que falta comprar."
            >
              <Link to="/menu">
                <Button variante="secundario">Ir al menú</Button>
              </Link>
            </EstadoVacio>
          )}

          {lista.platillosConsiderados > 0 && lista.faltantes.length === 0 && (
            <Banner tono="exito" titulo="No te falta nada">
              Con lo que tienes en inventario alcanza para todo el menú de este periodo.
            </Banner>
          )}

          {lista.faltantes.length > 0 && (
            <>
              <Card className="bg-superficie-suave">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-texto-suave">
                    {lista.faltantes.length}{' '}
                    {lista.faltantes.length === 1 ? 'insumo' : 'insumos'} por comprar
                  </span>
                  <span className="text-xl font-bold tabular-nums text-texto">
                    {formatearCentavos(lista.costoAproximadoCentavos)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-texto-tenue">
                  Costo aproximado con los precios de tus últimas compras
                  {lista.costoIncompleto && ', y falta el precio de algún insumo'}
                </p>
              </Card>

              <Seccion titulo="Falta comprar">
                <ul className="space-y-2">
                  {lista.faltantes.map((renglon) => (
                    <RenglonFaltante
                      key={renglon.ingredientId}
                      renglon={renglon}
                      periodo={periodo}
                      fecha={fecha}
                    />
                  ))}
                </ul>
              </Seccion>

              {lista.faltantes.some((r) => r.atendido) && (
                <Button
                  variante="fantasma"
                  onClick={() => void limpiarMarcas(periodo, fecha)}
                >
                  Desmarcar todo
                </Button>
              )}
            </>
          )}

          {lista.cubiertos.length > 0 && (
            <Seccion
              titulo="Ya lo tienes"
              descripcion="Se necesita, pero alcanza con tu inventario"
            >
              <ul className="space-y-2">
                {lista.cubiertos.map((renglon) => (
                  <li key={renglon.ingredientId}>
                    <Card>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-texto">{renglon.nombreInsumo}</p>
                          <p className="text-sm text-texto-suave">
                            Necesitas {renglon.necesarioLegible} · tienes{' '}
                            {renglon.disponibleLegible}
                          </p>
                        </div>
                        <span className="shrink-0 text-xs font-medium text-exito">✓</span>
                      </div>
                    </Card>
                  </li>
                ))}
              </ul>
            </Seccion>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsRenglon {
  renglon: RenglonCompra;
  periodo: PeriodoLista;
  fecha: string;
}

function RenglonFaltante({ renglon, periodo, fecha }: PropsRenglon) {
  const [abierto, setAbierto] = useState(false);

  return (
    <li>
      <Card className={renglon.atendido ? 'opacity-60' : ''}>
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={renglon.atendido}
            onChange={(e) =>
              void marcarAtendido(renglon.ingredientId, periodo, fecha, e.target.checked)
            }
            aria-label={`Marcar ${renglon.nombreInsumo} como comprado`}
            className="mt-0.5 size-5 shrink-0"
          />

          <div className="min-w-0 flex-1">
            <p
              className={`font-semibold text-texto ${
                renglon.atendido ? 'line-through' : ''
              }`}
            >
              {renglon.nombreInsumo}
            </p>

            <p className="text-base font-medium text-alerta">
              Falta {renglon.faltanteLegible}
            </p>

            <p className="text-xs text-texto-suave">
              Necesitas {renglon.necesarioLegible} · tienes {renglon.disponibleLegible}
            </p>

            {renglon.sugerenciaLegible !== null && (
              <p className="mt-1 text-xs text-texto-tenue">
                Serían {renglon.sugerenciaLegible}
              </p>
            )}

            <button
              type="button"
              onClick={() => setAbierto(!abierto)}
              className="mt-2 min-h-11 text-xs font-medium text-marca-fuerte"
            >
              {abierto ? 'Ocultar' : `¿Para qué? (${renglon.origenes.length})`}
            </button>

            {abierto && (
              <ul className="mt-1 space-y-1 border-l-2 border-borde pl-3">
                {renglon.origenes.map((origen, i) => (
                  <li key={`${origen.plannedItemId}-${i}`} className="text-xs text-texto-suave">
                    {formatearMedio(origen.fechaVenta)} · {origen.porciones}{' '}
                    {origen.porciones === 1 ? 'porción' : 'porciones'}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <span className="shrink-0 text-sm tabular-nums text-texto-suave">
            {renglon.costoAproximadoCentavos === null
              ? '—'
              : formatearCentavos(renglon.costoAproximadoCentavos)}
          </span>
        </div>
      </Card>
    </li>
  );
}
